'use client';

import {
  summarizeCodeGraphs,
  summarizeComponentVerification,
} from '@terra/ui-ai-applications';
import type { AiComponentDto } from '@terra/shared-contracts-ai-applications';
import { MetaDefault } from '@terra/ui-design-system';
import { VStack } from '@terra/ui-styled-system';
import { useMemo } from 'react';
import { useTranslations } from 'next-intl';

type AiDiscoveryCoverageProps = {
  components: AiComponentDto[];
};

export function AiDiscoveryCoverage({ components }: AiDiscoveryCoverageProps) {
  const t = useTranslations('discovery.aiApplication');
  const rollup = useMemo(
    () => summarizeComponentVerification(components),
    [components],
  );
  const codeGraphs = useMemo(
    () => summarizeCodeGraphs(components),
    [components],
  );

  if (components.length === 0) {
    return null;
  }

  return (
    <VStack gap={2} alignItems="stretch" w="full">
      <MetaDefault>
        {t('verificationRollup', {
          recorded: rollup.recorded,
          confirmed: rollup.confirmed,
          contradicted: rollup.contradicted,
          unverified: rollup.unverified,
        })}
      </MetaDefault>
      {codeGraphs.map((graph) => (
        <MetaDefault key={graph.graphId} color="fg.muted">
          {t('codeGraphSummary', {
            graphId: graph.graphId,
            entryFile: graph.entryFile ?? t('codeGraphEntryUnknown'),
            nodeCount: graph.nodeCount,
            tracedFileCount: graph.tracedFileCount,
            omittedCount: graph.omittedCount,
          })}
        </MetaDefault>
      ))}
    </VStack>
  );
}
